
















































































































































































































































import * as R from 'ramda';
import { MonitoringAPI } from '@/app/api';
import { TailwindTable, WeatherDynamicIcon } from '@/app/components';
import { useAxios, useDuration, useFilters } from '@/app/composable';
import { ColumnPosition, ColumnType, WorkflowType } from '@/app/constants';
import { MonitoringExecutionQuery } from '@/app/types';
import { CheckCircleIcon, DotsCircleHorizontalIcon, QuestionMarkCircleIcon, XCircleIcon } from '@vue-hero-icons/solid';
import { PropType, Ref, computed, defineComponent, ref, watch } from '@vue/composition-api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ExecutionStatus } from '../../constants';
import ExecutionError from './ExecutionError.vue';
import MostCommonFailedBlock from './MostCommonFailedBlock.vue';

dayjs.extend(relativeTime);

export default defineComponent({
    name: 'IncidentPerAnalyticsPipeline',
    props: {
        query: { type: Object as PropType<MonitoringExecutionQuery>, required: true },
    },
    components: {
        TailwindTable,
        CheckCircleIcon,
        XCircleIcon,
        DotsCircleHorizontalIcon,
        WeatherDynamicIcon,
        ExecutionError,
        QuestionMarkCircleIcon,
        MostCommonFailedBlock,
    },
    setup(props, { root, emit }) {
        const { exec, loading } = useAxios(true);
        const { fromNow: dateFromNow, formatDateAs: formatDate } = useFilters();
        const { formatToHighestMetric } = useDuration();

        const analytics: Ref<any> = ref<any>();

        const fetchAnalytics = async (query: MonitoringExecutionQuery) => {
            await exec(MonitoringAPI.executionIncidents(query, WorkflowType.Analytics))
                .then((res: any) => {
                    analytics.value = res.data;
                    emit('update-show-results-warning', res.data.pagination.total >= 1000);
                })
                .catch((e) => {
                    (root as any).$toastr.e(e.response.data.message, 'Error');
                    throw e;
                });
        };

        const columns = computed(() => [
            {
                key: 'id',
                label: 'Pipeline ID',
                type: ColumnType.Uid,
                showAfter: 'sm',
                copy: true,
                link: (value: number, row: any) => {
                    return value
                        ? {
                              name: 'history',
                              params: { workflowId: `${value}` },
                              query: {
                                  executionId: row.executionId,
                              },
                          }
                        : null;
                },
            },
            {
                key: 'name',
                label: 'Pipeline',
                type: ColumnType.Text,
            },
            {
                key: 'failedExecutions',
                label: 'Problematic Executions',
                type: ColumnType.Integer,
                showAfter: 'md',
                position: ColumnPosition.Left,
                sortable: {
                    enabled: true,
                    note: `Problematic Executions are always sorted by failed executions`,
                    key: 'failures',
                },
            },

            {
                key: 'totalExecutionsAverageTime',
                label: 'Avg Total Execution Time',
                type: ColumnType.Integer,
                showAfter: 'xl',
                position: ColumnPosition.Center,
                sortable: {
                    enabled: true,
                    key: 'totalAverageTime',
                },
            },
            {
                key: 'failedExecutionsAverageTime',
                label: 'Avg Failed Execution Time',
                type: ColumnType.Integer,
                showAfter: 'xl',
                position: ColumnPosition.Center,
                sortable: {
                    enabled: true,
                    key: 'failedAverageTime',
                },
            },
            {
                key: 'errors',
                label: 'Most Common Failure Reason',
                type: ColumnType.Array,
                showAfter: 'xl',
            },
            {
                key: 'failedBlocks',
                label: 'Most Common Failed Block',
                type: ColumnType.Array,
                showAfter: 'xl',
            },
            {
                key: 'lastExecution',
                label: 'Last Execution',
                type: ColumnType.Object,
            },
        ]);

        const rows = computed(() => analytics.value?.results);
        const rejectedItems = computed(
            () => (analytics.value?.accessControl?.rejectedItems ?? 0) + (analytics.value?.deletedPipelines ?? 0),
        );
        const pagination = computed(() => analytics.value?.pagination);

        const changeSort = (sortBy: { field: string; asc: boolean }) => {
            emit('sort', sortBy);
        };

        const executionTime = (t: number) => formatToHighestMetric(t * 1000);

        watch(
            () => props.query,
            (query: MonitoringExecutionQuery, oldQuery: MonitoringExecutionQuery | undefined) => {
                if (!oldQuery || JSON.stringify(oldQuery) !== JSON.stringify(query)) fetchAnalytics(R.clone(query));
            },
            { deep: true, immediate: true },
        );

        watch(
            () => loading.value,
            (l: boolean) => emit('loading', l),
            { immediate: true },
        );

        return {
            rows,
            pagination,
            loading,
            columns,
            rejectedItems,
            ExecutionStatus,
            dateFromNow,
            formatDate,
            R,
            changeSort,
            executionTime,
        };
    },
});
